import React, { useContext, useEffect, useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import { NavLink, useHistory } from "react-router-dom";

import claimApi from "../../api/claim";
import { Loader, toast } from "../../common/components/core";
import { FormContainer, FormFooter } from "../../common/components/form";
import { useAuthErrorHandlerCreator } from "../../common/hooks";
import { BesuProjectState } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";
import { shortenUUID } from "../../utils/common";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
} from "../../utils/errorHandling";
import ProjectContext from "../project/ProjectContext";

import ClaimForm, { ClaimFormValues } from "./components/ClaimForm";
import ClaimPreview from "./components/ClaimPreview";

const NewClaimView = (): JSX.Element => {
    document.title = PageTitle.NewClaimView;
    const {
        data: project,
        mutate: mutateProject,
    } = useContext(ProjectContext);
    const history = useHistory();
    const onAuthError = useAuthErrorHandlerCreator();
    const [isSubmitting, setSubmitting] = useState(false);

    const submit = async (values: ClaimFormValues) => {
        setSubmitting(true);
        try {
            const res = await claimApi.create(project.referenceId, values);
            const newClaimId = res.stateRefMap?.referenceId?.[0];
            if (!newClaimId) {
                throw new Error("Claim on-chain id is missing from response");
            }
            toast(`Claim #${shortenUUID(newClaimId ?? "")} submitted`);
            mutateProject();
            history.push(`/project/${project.referenceId}/claim/${newClaimId}`);
        } catch (e) {
            setSubmitting(false);
            composeErrorHandlers(
                onStaleStateError(() => {
                    // no toast because project deletion has error popup already
                    mutateProject();
                    history.push(`/project/${project.referenceId}`);
                }),
                onAuthError,
            )(e as GenericError);
        }
    };

    return (
        <PureNewClaimView
            project={project}
            isLoading={isSubmitting}
            onSubmit={submit}
            onCancel={() => history.push(`/project/${project.referenceId}`)}
        />
    );
};

type PureNewClaimViewProps = {
    project: BesuProjectState;
    isLoading: boolean;
    onSubmit: (values: ClaimFormValues) => void;
    onCancel: () => void;
};

export const PureNewClaimView = ({
    project,
    isLoading,
    onCancel,
    onSubmit,
}: PureNewClaimViewProps): JSX.Element => {
    const [isPreview, setIsPreview] = useState(false);
    const [formValues, setFormValues] = useState<ClaimFormValues>();

    useEffect(() => {
        if (!isLoading) {
            setIsPreview(false);
        }
    }, [isLoading]);

    const preview = (values: ClaimFormValues) => {
        setFormValues(values);
        setIsPreview(true);
    };

    return (
        <Loader overlay isLoading={isLoading} bgClassName="bg-body">
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/project">Project List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink to={`/project/${project.referenceId}`}>
                        {project.name}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="fw-bold">
                    New Claim
                </Breadcrumb.Item>
            </Breadcrumb>
            {formValues && isPreview ? (
                <>
                    <h1 className="mb-3">Preview</h1>
                    <ClaimPreview
                        formValues={formValues}
                        claimApprovers={project.claimApprovers}
                    />
                    <FormFooter className="d-flex justify-content-between">
                        <Button
                            variant="light.tertiary"
                            onClick={() => setIsPreview(false)}
                        >
                            Back
                        </Button>
                        <Button
                            variant="light.primary"
                            onClick={() => onSubmit(formValues)}
                        >
                            Submit
                        </Button>
                    </FormFooter>
                </>
            ) : (
                <FormContainer>
                    <h1 className="mb-3 rem-1-5">Enter Details for Your New Claim</h1>
                    <ClaimForm
                        values={formValues}
                        onCancel={onCancel}
                        onSubmit={preview}
                    />
                </FormContainer>

            )}
        </Loader>
    );
};

export default NewClaimView;
